import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    PaperAirplaneIcon, 
    UserCircleIcon, 
    CheckBadgeIcon,
    PhotoIcon,
    FaceSmileIcon
} from '@heroicons/react/24/solid';
import { db, auth } from '../../firebase'; // Link with Al-Baron Firebase
import { collection, addDoc, query, orderBy, onSnapshot, serverTimestamp, doc, getDoc, setDoc } from 'firebase/firestore';

const StudentChat = () => {
    const [message, setMessage] = useState("");
    const [messages, setMessages] = useState([]);
    const [userData, setUserData] = useState(null);
    const scrollRef = useRef(null);
    // 🔔 [NOTIFICATION_AUDIO] - إعداد ملف الصوت للتنبيهات الواصلة من المدرس
    const audioRef = useRef(new Audio('/notification.mp3')); 

    // [خارطة برمجية]: 1. (جلب بيانات الطالب وجلب الرسائل مع نظام التنبيه الذكي المفلتر)
    useEffect(() => {
        const fetchUser = async () => {
            if (auth.currentUser) {
                const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
                if (userDoc.exists()) setUserData(userDoc.data());
            }
        };
        fetchUser();

        if (auth.currentUser) {
            const q = query(
                collection(db, "chats", auth.currentUser.uid, "messages"), 
                orderBy("createdAt", "asc")
            );
            
            const unsubscribe = onSnapshot(q, (snapshot) => {
                const fetchedMessages = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                // 🧠 [AL-BARON_SMART_FILTER] - الفلتر الصارم لمنع رجوع التنبيه للمرسل
                snapshot.docChanges().forEach((change) => {
                    if (change.type === "added") {
                        const msgData = change.doc.data();
                        
                        // 🛡️ [GOLDEN_FIX]: الصوت يشتغل "فقط" إذا كان المرسل هو المدرس
                        // ذكاء البارون: نتحقق من الرتبة لضمان عدم إزعاج الطالب برسائله الخاصة
                        if (msgData.senderRole === 'teacher') {
                            audioRef.current.play().catch(e => console.log("Audio play deferred"));
                            
                            // ✨ [NEW_SMART_NOTIFY]: إظهار إشعار نظام فقط إذا كانت الرسالة من المدرس والتطبيق بالخلفية
                            if ("Notification" in window && Notification.permission === "granted" && document.hidden) {
                                new Notification("نور أكاديمي", {
                                    body: `👨‍🏫 المدرس أرسل لك رسالة جديدة`,
                                    icon: "/logo192.png"
                                });
                            }
                        }
                    }
                });

                setMessages(fetchedMessages);
            });
            return unsubscribe;
        }
    }, []);

    // سكرول تلقائي للمحافظة على سلاسة العرض
    useEffect(() => {
        scrollRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    // [دالة الإرسال الملكية]: 2. (معالجة الإرسال وتنبيه المدرس مع تنظيف فوري للصندوق)
    const handleSend = async (e) => {
        e.preventDefault();
        const currentMsg = message.trim();
        
        if (!currentMsg || !auth.currentUser) return;

        // ✅ [AL-BARON_CLEAN]: تنظيف الصندوق فوراً لتوفير أفضل تجربة مستخدم للطالب
        setMessage(""); 

        const studentName = userData?.name || "طالب النور";

        try {
            // أ. تحديث حالة المحادثة الرئيسية للمدرس
            await setDoc(doc(db, "chats", auth.currentUser.uid), {
                studentName: studentName,
                lastMessage: currentMsg,
                updatedAt: serverTimestamp(),
                studentId: auth.currentUser.uid,
                status: "unread" 
            }, { merge: true });

            // ب. إضافة الرسالة في كولكشن المحادثة الخاص بالطالب
            await addDoc(collection(db, "chats", auth.currentUser.uid, "messages"), {
                text: currentMsg,
                senderRole: "student",
                senderId: auth.currentUser.uid,
                createdAt: serverTimestamp(),
                time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            });

            // ج. تنبيه المدرس من خلال كولكشن الإشعارات العام (المدرس هو من سيستلم الإشعار هناك)
            // ج. تنبيه المدرس (مع التعديل الباروني لضمان الظهور)
            await addDoc(collection(db, "notifications"), {
                text: `رسالة جديدة من : ${studentName}`,
                type: 'chat',
                targetType: 'private', // ✅ أضفنا هذا السطر حتى الـ Navbar يفهم إنه خاص
                createdAt: serverTimestamp(),
                role: 'teacher',
                targetId: 'mOKBJYKFVSTZqA4amknV9Z3S08L2', 
                isRead: false,
                studentId: auth.currentUser.uid
            });

            // ⚠️ [AL-BARON_REMOVED]: تم حذف أمر Notification من هنا لمنع ظهور إشعار للطالب عند إرساله هو للرسالة.

        } catch (error) {
            console.error("Error notifying teacher:", error);
            // في حال الفشل النادر، نعيد الرسالة للصندوق حتى لا تضيع
            setMessage(currentMsg);
        }
    };

    return (
        <div style={{ fontFamily: '"Cairo", sans-serif' }} 
             className="h-[calc(100vh-140px)] max-w-4xl mx-auto ios-glass flex flex-col rounded-[35px] border border-white/30 dark:border-white/10 bg-white/20 dark:bg-[#1C1C1E]/60 backdrop-blur-3xl shadow-2xl overflow-hidden transition-colors duration-500 animate-in fade-in" dir="rtl">
            
            {/* 🏰 [ROYAL_HEADER] - الهيدر الملكي للمحادثة */}
            <div className="p-5 bg-white/30 dark:bg-white/5 border-b border-white/20 dark:border-white/10 flex items-center justify-between z-10 backdrop-blur-xl">
                <div className="flex items-center gap-4">
                    <div className="relative">
                        <div className="w-12 h-12 bg-gradient-to-tr from-[#007AFF] to-[#00C7FF] rounded-2xl flex items-center justify-center text-white shadow-lg overflow-hidden border border-white/20">
                            <UserCircleIcon className="w-10 h-10" />
                        </div>
                        <span className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 border-4 border-white dark:border-[#1C1C1E] rounded-full shadow-sm"></span>
                    </div>
                    <div>
                        <h3 className="font-black text-gray-900 dark:text-white text-base flex items-center gap-1.5 leading-none">
                            الأستاذة نور عماد <CheckBadgeIcon className="w-4 h-4 text-[#007AFF]" />
                        </h3>
                        <p className="text-[10px] text-emerald-500 font-bold uppercase tracking-widest flex items-center gap-1 mt-1.5">
                            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span> متصلة الآن
                        </p>
                    </div>
                </div>
                <div className="hidden md:block">
                    <span className="text-[9px] font-black text-gray-500 dark:text-gray-400 bg-white/40 dark:bg-white/5 px-4 py-2 rounded-full border border-white/20 dark:border-white/10 shadow-sm uppercase tracking-tighter">
                        Fast Response Active
                    </span>
                </div>
            </div>

            {/* 💬 [MESSAGES_AREA] - منطقة الرسائل باللون الأزرق الملكي */}
            <div className="flex-1 overflow-y-auto custom-scroll p-6 space-y-6 dark:bg-black/10">
                {messages.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-gray-400 font-bold text-sm">ابدأ المحادثة مع الأستاذة نور...</div>
                ) : (
                    messages.map((msg) => (
                        <motion.div 
                            key={msg.id}
                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            className={`flex ${msg.senderRole === 'student' ? 'justify-start' : 'justify-end'}`}
                        >
                            {/* [تعديل رويال]: فقاعة الطالب باللون الأزرق الملكي اللامع */}
                            <div className={`max-w-[85%] md:max-w-[70%] p-4 rounded-[24px] shadow-md relative border backdrop-blur-lg ${
                                msg.senderRole === 'student' 
                                ? 'bg-[#007AFF] text-white rounded-br-none border-white/40 shadow-blue-500/20' 
                                : 'bg-white/90 dark:bg-white/15 text-gray-900 dark:text-white rounded-bl-none border-white/50 dark:border-white/10 shadow-black/5'
                            }`}>
                                <p className="text-sm font-black leading-relaxed tracking-tight">{msg.text}</p>
                                <div className={`flex items-center gap-1 mt-2 ${msg.senderRole === 'student' ? 'justify-start text-white/80' : 'justify-end text-gray-500 dark:text-white/60'}`}>
                                    <span className="text-[9px] font-black">{msg.time}</span>
                                    {msg.senderRole === 'student' && <CheckBadgeIcon className="w-3.5 h-3.5 text-white" />}
                                </div>
                            </div>
                        </motion.div>
                    ))
                )}
                <div ref={scrollRef} />
            </div>

            {/* ✍️ [INPUT_BAR] - شريط الإدخال بإطار أزرق ملكي واضح */}
            <div className="p-5 bg-white/40 dark:bg-white/5 border-t border-white/20 dark:border-white/10 backdrop-blur-xl">
                <form onSubmit={handleSend} className="flex items-center gap-3 bg-white/80 dark:bg-black/60 p-2 px-3 rounded-[28px] border-2 border-[#007AFF] shadow-[0_0_15px_rgba(0,122,255,0.2)]">
                    <button type="button" className="p-2 text-gray-500 dark:text-gray-400 hover:text-[#007AFF] transition-all hover:scale-110 active:scale-90"><PhotoIcon className="w-6 h-6"/></button>
                    <button type="button" className="p-2 text-gray-500 dark:text-gray-400 hover:text-[#007AFF] transition-all hover:scale-110 active:scale-90 hidden sm:block"><FaceSmileIcon className="w-6 h-6"/></button>
                    
                    <input 
                        type="text" 
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="اسأل الأستاذة نور هنا..." 
                        className="flex-1 bg-transparent border-none py-3 px-2 outline-none font-black text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 text-right" 
                    />
                    
                    {/* زر الإرسال باللون الأزرق الملكي المطابق */}
                    <button 
                        type="submit"
                        className="w-11 h-11 bg-[#007AFF] text-white rounded-full flex items-center justify-center shadow-lg shadow-blue-500/40 hover:scale-105 active:scale-90 transition-all group"
                    >
                        <PaperAirplaneIcon className="w-5 h-5 -rotate-45 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </button>
                </form>
            </div>
        </div>
    );
};

export default StudentChat;